/**
 * Company: SPHMedia
 * Description: Hook to manage inline login user information and visitor category cookie.
 */

import { userSchema, UserStatus } from "@app/schemas/mySphUser";
import { isClientSide } from "@helper/utils";
import { useMySPHLightbox } from "@sphtech/idpf-lightbox-react";
import { getUserType, getVisitorCat } from "@src/middleware/analytics";
import Cookies from "js-cookie";
import { useEffect } from "react";

/**
 * Custom React Hook: useInlineLoginUserInfoHook
 * Description: This hook manages the inline login user information and updates the mysph user cookies
 * based on the user's login ID, service flags, visitor ID and user status.
 * It uses the {@link useMySPHLightbox} hook to access the `userInfo` and updates the cookies accordingly.
 * {@link userSchema} is used to validate the user information before updating the cookies.
 */
export default function useInlineLoginUserInfo() {
  const { userInfo: rawUserInfo } = useMySPHLightbox();

  useEffect(() => {
    if (!isClientSide || !rawUserInfo) return;

    const parsedUserInfo = userSchema.safeParse(rawUserInfo);

    if (!parsedUserInfo.success) return;

    const userInfo = parsedUserInfo.data;

    const loginId = userInfo.loginId ?? "";
    const serviceFlags = userInfo.serviceFlags ?? [];
    const userStatus = userInfo.userStatus ?? UserStatus.None;
    const visitorId = userInfo.visitorId ?? "";

    const userType = getUserType(loginId, serviceFlags);

    const visitorCat = getVisitorCat({
      userType,
      serviceFlags,
      userStatus,
      loginId: loginId,
    });

    const svc = serviceFlags.join("|");

    const cookiesList = [
      { name: "svc", value: svc },
      { name: "mySPHUserType", value: userType },
      { name: "mysphw", value: visitorId },
      { name: "loginid", value: loginId },
      { name: "visitorcat", value: visitorCat.toString() },
    ];

    cookiesList.forEach(({ name, value }) => {
      if (Cookies.get(name) !== value) {
        Cookies.set(name, value, {
          expires: 1 / 24,
          path: "/",
          secure: true,
          sameSite: "strict",
        });
      }
    });

    if (typeof window._data !== "undefined") {
      if (window._data.visitorcat !== visitorCat) {
        window._data.visitorcat = visitorCat;
      }

      if (window._data.svc !== svc) {
        window._data.svc = svc;
      }

      if (window._data.at !== visitorId) {
        window._data.at = visitorId;
      }
    }
  }, [rawUserInfo]);
}
